<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import Icon from 'next/icon/Icon.vue';
import { useSidebarContext } from './provider';
import { isChildAccessible } from './isChildAccessible';
import {
  SIDEBAR_MENU_ICON_ACTIVE,
  SIDEBAR_MENU_ICON_INACTIVE,
  SIDEBAR_MENU_ITEM_ACTIVE,
  SIDEBAR_MENU_ITEM_INACTIVE,
} from './sidebarMenuItemClasses';

const props = defineProps({
  items: { type: Array, required: true },
});

const route = useRoute();
const router = useRouter();
const { resolvePath, resolvePermissions, resolveFeatureFlag, isAllowed } =
  useSidebarContext();

const navigableChildren = item =>
  item.children?.flatMap(child => child.children || child) || [];

const isChildActive = child => {
  if (!child.to) return false;
  if (route.path === resolvePath(child.to)) return true;
  return child.activeOn?.includes(route.name);
};

const isItemActive = item => {
  if (item.to) {
    if (route.path === resolvePath(item.to)) return true;
    return item.activeOn?.includes(route.name);
  }

  if (item.children?.length) {
    return navigableChildren(item).some(child => isChildActive(child));
  }

  return false;
};

const isItemAccessible = item => {
  if (item.onClick) return true;
  if (item.to) return isAllowed(item.to);
  if (!item.children?.length) return false;

  return item.children.some(child => {
    if (child.children) {
      return child.children.some(nestedChild =>
        isChildAccessible(nestedChild, isAllowed)
      );
    }
    return isChildAccessible(child, isAllowed);
  });
};

const accessibleItems = computed(() =>
  props.items.filter(item => isItemAccessible(item))
);

const resolveAccessibleChild = child => {
  if (child.children) {
    return child.children.find(nestedChild =>
      isChildAccessible(nestedChild, isAllowed)
    );
  }
  return isChildAccessible(child, isAllowed) ? child : undefined;
};

const firstAccessibleChild = item => {
  if (!item.children?.length) return null;
  return item.children.map(resolveAccessibleChild).find(Boolean) ?? null;
};

const executeItemAction = target => {
  if (target.onClick) {
    target.onClick();
    return;
  }

  if (target.to) {
    router.push(target.to);
  }
};

const handleClick = item => {
  if (item.onClick || item.to) {
    executeItemAction(item);
    return;
  }

  const firstChild = firstAccessibleChild(item);
  if (firstChild) {
    executeItemAction(firstChild);
  }
};
</script>

<template>
  <ul class="flex flex-col items-center gap-0 m-0 p-0 list-none w-full">
    <Policy
      v-for="item in accessibleItems"
      :key="item.name"
      :permissions="resolvePermissions(item.to)"
      :feature-flag="resolveFeatureFlag(item.to)"
      as="li"
      class="w-full"
    >
      <RouterLink
        v-if="item.to && !item.onClick"
        v-tooltip.right="item.label"
        class="group flex items-center justify-center w-full h-10 transition-colors ltr:border-l-[3px] rtl:border-r-[3px]"
        :class="[
          isItemActive(item)
            ? SIDEBAR_MENU_ITEM_ACTIVE
            : SIDEBAR_MENU_ITEM_INACTIVE,
        ]"
        :to="item.to"
      >
        <Icon
          v-if="item.icon"
          :icon="item.icon"
          class="size-5"
          :class="
            isItemActive(item)
              ? SIDEBAR_MENU_ICON_ACTIVE
              : SIDEBAR_MENU_ICON_INACTIVE
          "
        />
      </RouterLink>
      <button
        v-else
        v-tooltip.right="item.label"
        type="button"
        class="group flex items-center justify-center w-full h-10 transition-colors ltr:border-l-[3px] rtl:border-r-[3px]"
        :class="[
          isItemActive(item)
            ? SIDEBAR_MENU_ITEM_ACTIVE
            : SIDEBAR_MENU_ITEM_INACTIVE,
        ]"
        @click="handleClick(item)"
      >
        <Icon
          v-if="item.icon"
          :icon="item.icon"
          class="size-5"
          :class="
            isItemActive(item)
              ? SIDEBAR_MENU_ICON_ACTIVE
              : SIDEBAR_MENU_ICON_INACTIVE
          "
        />
      </button>
    </Policy>
  </ul>
</template>
